import { useEffect, useState, type ReactNode } from 'react';
import { oneliveAssets } from './assets';
import {
  fetchCurrentMemberProfile,
  fetchItemShopWallet,
  formatAmount,
  navigate,
  resolveDisplayNickname,
  type OneliveSessionUser,
} from './api';
import {
  buildMypageProfilePath,
  isMypageSectionPath,
  isMypageMenuActive,
  MYPAGE_MENU_ITEMS,
} from './mypageMenu';
import { resolveMemberLevel } from './memberLevel';
import { useOneliveSession } from './useOneliveSession';

interface Props {
  children?: ReactNode;
}

const DEFAULT_AVATAR = '/img/no_profile.svg';

function readPathname() {
  return window.location.pathname.replace(/\/$/, '') || '/';
}

export default function OneliveMobileMembersPanel({ children }: Props) {
  const { user: sessionUser, isAuthed, authReady } = useOneliveSession();
  const [user, setUser] = useState<OneliveSessionUser | null>(() => sessionUser);
  const [coinBalance, setCoinBalance] = useState(0);
  const [walletDisplayName, setWalletDisplayName] = useState('');
  const [messageCount] = useState(0);
  const [pathname, setPathname] = useState(readPathname);

  useEffect(() => {
    setUser(sessionUser);
  }, [sessionUser]);

  useEffect(() => {
    const onNav = () => setPathname(readPathname());
    window.addEventListener('popstate', onNav);
    const timer = window.setInterval(onNav, 500);
    return () => {
      window.removeEventListener('popstate', onNav);
      window.clearInterval(timer);
    };
  }, []);

  useEffect(() => {
    if (!authReady) return;

    let mounted = true;

    const load = async () => {
      if (!isAuthed) {
        if (mounted) {
          setCoinBalance(0);
          setWalletDisplayName('');
        }
        return;
      }

      const [wallet, profile] = await Promise.all([
        fetchItemShopWallet(),
        fetchCurrentMemberProfile(),
      ]);

      if (!mounted) return;

      if (wallet) {
        setCoinBalance(wallet.coin_balance ?? 0);
        if (wallet.display_name) setWalletDisplayName(wallet.display_name);
      }

      if (profile?.uuid) {
        setUser((prev) => ({
          ...sessionUser,
          ...prev,
          ...profile,
          name: profile.name || prev?.name || sessionUser?.name,
          nickname: profile.nickname || prev?.nickname || wallet?.display_name || sessionUser?.nickname,
          avatar: profile.avatar || profile.member_profile?.avatar_url || prev?.avatar || sessionUser?.avatar,
        }));
      } else {
        setUser((prev) => ({
          ...sessionUser,
          ...prev,
          name: prev?.name || sessionUser?.name || wallet?.display_name,
          nickname: prev?.nickname || sessionUser?.nickname || wallet?.display_name,
        }));
      }
    };

    void load();
    return () => { mounted = false; };
  }, [authReady, isAuthed, pathname, sessionUser]);

  const effectiveUser = sessionUser?.uuid ? { ...sessionUser, ...user } : user;
  const memberLevel = resolveMemberLevel(
    effectiveUser?.member_level,
    effectiveUser?.member_level_label,
    effectiveUser,
  );
  const displayNickname = resolveDisplayNickname(effectiveUser, walletDisplayName);
  const avatarUrl = effectiveUser?.avatar?.trim() || DEFAULT_AVATAR;
  const showSectionContent = isMypageSectionPath(pathname);
  const membersClass = ['members', 'onelive-members', 'mar-cash'].join(' ');

  const go = (path: string) => navigate(path);

  const openCoinHistory = () => {
    go(buildMypageProfilePath('coin'));
  };

  const onMenuClick = (item: (typeof MYPAGE_MENU_ITEMS)[number]) => {
    go(item.path);
  };

  const renderMenuItem = (item: (typeof MYPAGE_MENU_ITEMS)[number]) => {
    const active = isMypageMenuActive(item, pathname);
    return (
      <li key={item.path} className={active ? 'is-active' : undefined}>
        <button type="button" onClick={() => onMenuClick(item)}>
          {item.label}
        </button>
      </li>
    );
  };

  return (
    <>
      <div className={membersClass}>
        <div className="side-head">
          <div className="top-grid">
            <div className="onelive-mypage-user-block">
              <span className="onelive-mypage-user-img">
                <img src={avatarUrl} alt="" />
              </span>
              <em className="onelive-mypage-user-em">
                <strong className="onelive-mypage-user-nick">{displayNickname}</strong>
                <span className="onelive-mypage-user-level">({memberLevel.label})</span>
              </em>
            </div>
            <div className="onelive-mypage-message-col">
              <button type="button" className="onelive-mypage-message-link" onClick={() => go('/mypage/messages')}>
                <img src={oneliveAssets.drawerMessage} alt="" className="onelive-mypage-message-icon" />
                <span>쪽지함 ({messageCount})</span>
              </button>
            </div>
          </div>

          <div className="side-cash">
            <div className="onelive-mypage-coin-label">
              <span className="onelive-mypage-coin-icon" aria-hidden="true">
                <img src={oneliveAssets.drawerCoin} alt="" />
              </span>
              <span>보유 코인</span>
            </div>
            <button
              type="button"
              className="onelive-mypage-coin-value onelive-mypage-coin-link"
              onClick={openCoinHistory}
            >
              <strong className="MyMoney">{formatAmount(coinBalance)}</strong>
              <span className="onelive-mypage-coin-unit">원</span>
            </button>
          </div>
        </div>

        <div className="side-body onelive-members-side-body">
          <ul className="menubar onelive-members-menubar">
            {MYPAGE_MENU_ITEMS.map(renderMenuItem)}
          </ul>

          {showSectionContent && children ? (
            <div className="onelive-mypage-content onelive-mypage-content--section">
              {children}
            </div>
          ) : null}
        </div>
      </div>
    </>
  );
}
